import { useEffect, useRef, useState } from 'react';


type UseFileUploadProps = {
	onFilesChange: (files: File[]) => void;
	onDeleteExistingPhoto: (photoName: string) => void;
	resetKey: number;
};

export const useFileUpload = ({
	onFilesChange,
	onDeleteExistingPhoto,
	resetKey,
}: UseFileUploadProps) => {
	const [files, setFiles] = useState<File[]>([]);
	// Ref keeps the current files synchronously accessible without closure staleness
	const filesRef = useRef<File[]>([]);
	// Skip the initial mount: only notify the parent when resetKey actually changes
	const isMounted = useRef(false);

	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			return;
		}
		filesRef.current = [];
		setFiles([]);
		onFilesChange([]);
	}, [resetKey]);

	const handleFiles = (event: {
		persist: () => void;
		target: { files: File[] | FileList };
	}) => {
		event.persist();
		if (!event?.target?.files) return;
		const incoming = Array.from(event.target.files) as File[];
		if (incoming.length === 0) return;
		const updated = [...filesRef.current, ...incoming];
		filesRef.current = updated;
		setFiles(updated);
		onFilesChange(updated);
	};

	const handleDeleteLocalFile = (_event: unknown, fileName: string) => {
		const updated = filesRef.current.filter((f) => f.name !== fileName);
		filesRef.current = updated;
		setFiles(updated);
		onFilesChange(updated);
	};

	const handleDeleteExistingPhoto = (_event: unknown, fileName: string) => {
		onDeleteExistingPhoto(fileName);
	};

	return { files, handleDeleteExistingPhoto, handleDeleteLocalFile, handleFiles };
};
